import React, { createContext, useReducer, useContext, useState, useEffect, forwardRef, useImperativeHandle, createRef, useRef, useCallback, useMemo } from 'react';
import { useIsMounted, classNames } from '@ct-react/core';
import { TileLayer as TileLayer$1, FeatureGroup, markerClusterGroup, DivIcon, Control, Map, Popup as Popup$1, Marker as Marker$1 } from 'leaflet';
import { renderToString } from 'react-dom/server';
import 'leaflet.markercluster';
import { IconPlus, IconMinus } from '@tabler/icons-react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var DEFAULT_TILE = {
    url: "https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}",
    token: "pk.eyJ1IjoiYW5jYWxpb24iLCJhIjoiY2pwYmF6MGl3MDhpajNrdDZjYnB1OHdndyJ9.8MiaxCf5Tw-PIySTCGvGRw"
};

var MapRefContext = React.createContext(undefined);
var MapRefProvider = MapRefContext.Provider;

// tile layers
var tileLayersReducer = function (state, action) {
    switch (action.type) {
        case "ADD_TILE_LAYER":
            return __spreadArray(__spreadArray([], state, true), [
                action.layerRef
            ], false);
        case "REMOVE_TILE_LAYER":
            return __spreadArray([], state.filter(function (cl) { return cl !== action.layerRef; }), true);
        default:
            return state;
    }
};
// content layers
var contentLayersReducer = function (state, action) {
    switch (action.type) {
        case "ADD_CONTENT_LAYER":
            return __spreadArray(__spreadArray([], state, true), [
                action.layer
            ], false);
        case "REMOVE_CONTENT_LAYER":
            return __spreadArray([], state.filter(function (cl) { return cl !== action.layer; }), true);
        default:
            return state;
    }
};

var initialState = {
    tileLayers: [],
    contentLayers: []
};
var mainReducer = function (_a, action) {
    var tileLayers = _a.tileLayers, contentLayers = _a.contentLayers;
    return ({
        tileLayers: tileLayersReducer(tileLayers, action),
        contentLayers: contentLayersReducer(contentLayers, action)
    });
};
var MapLayersContext = createContext({
    layersState: initialState,
    dispatch: function () { return void 0; }
});
var MapLayersProvider = function (_a) {
    var children = _a.children;
    var _b = useReducer(mainReducer, initialState), state = _b[0], dispatch = _b[1];
    return (React.createElement(MapLayersContext.Provider, { value: { layersState: state, dispatch: dispatch } }, children));
};

var useMapRefContext = function () {
    var context = useContext(MapRefContext);
    if (!context)
        throw new Error("No or empty map context provided");
    return context;
};
var useMapLayersContext = function () { return useContext(MapLayersContext); };

var TileLayer = function (_a) {
    var label = _a.label, url = _a.url, options = __rest(_a, ["label", "url"]);
    var leafletMap = useMapRefContext();
    var dispatch = useMapLayersContext().dispatch;
    var layer = useState(new TileLayer$1(url, __assign(__assign({}, options), (!options.tileSize && !options.zoomOffset) && { tileSize: 512, zoomOffset: -1 })))[0];
    // add layer to option list only, the activation and selection of tile layer is handle by the layer-control
    useEffect(function () {
        leafletMap.options.layers.push(layer);
        dispatch({ type: "ADD_TILE_LAYER", layerRef: { label: label, layer: layer } });
    }, [layer]);
    // remove layer on destroy
    useEffect(function () {
        return function () {
            if (leafletMap.hasLayer(layer))
                leafletMap.removeLayer(layer);
            dispatch({ type: "REMOVE_TILE_LAYER", layerRef: { label: label, layer: layer } });
        };
    }, []);
    return null;
};

var styles = {"container":"ct-map__container","zoomInnerLink":"ct-map__zoomInnerLink","controlLayerWrapper":"ct-map__controlLayerWrapper","layerInnerButton":"ct-map__layerInnerButton","layerActiveInnerButton":"ct-map__layerActiveInnerButton","markerWrapper":"ct-map__markerWrapper","clusterWrapper":"ct-map__clusterWrapper","markerInner":"ct-map__markerInner","defaultInnerMarker":"ct-map__defaultInnerMarker","defaultClusterInnerMarker":"ct-map__defaultClusterInnerMarker","popupWrapper":"ct-map__popupWrapper"};

var iconBuilder$1 = function (content) { return new DivIcon({
    className: styles.clusterWrapper,
    html: renderToString(React.createElement("div", { className: styles.markerInner }, content))
}); };
var GroupedContent = function (_a, forwardedRef) {
    var fitBounds = _a.fitBounds, _b = _a.clusterize, clusterize = _b === void 0 ? false : _b, children = _a.children; _a.onResize;
    var typedGroup = !clusterize
        ? new FeatureGroup([])
        : markerClusterGroup({
            showCoverageOnHover: false,
            iconCreateFunction: function (cluster) {
                cluster.options.riseOnHover = true;
                if (typeof clusterize === "function")
                    return iconBuilder$1(clusterize(cluster));
                return iconBuilder$1(React.createElement("div", { className: styles.defaultClusterInnerMarker }, cluster.getChildCount()));
            }
        });
    var leafletMap = useMapRefContext();
    var layersState = useMapLayersContext().layersState;
    var group = useState(typedGroup)[0];
    var applyBounds = function () {
        if (!fitBounds || group.getLayers().length === 0)
            return;
        var bounds = group.getBounds();
        var newZoom = leafletMap.getBoundsZoom(bounds);
        leafletMap.fitBounds(bounds, __assign({ maxZoom: newZoom }, (typeof fitBounds === "number") && { padding: [fitBounds, fitBounds] }));
    };
    useImperativeHandle(forwardedRef, function () { return ({
        boundsOnResize: function () { return applyBounds(); }
    }); });
    useEffect(function () {
        leafletMap.addLayer(group);
    }, [group]);
    useEffect(function () {
        return function () {
            if (leafletMap.hasLayer(group))
                leafletMap.removeLayer(group);
        };
    }, []);
    useEffect(function () {
        var state = group.getLayers();
        var addable = layersState.contentLayers.filter(function (cl) { return !state.includes(cl); });
        var removable = state.filter(function (cl) { return !layersState.contentLayers.includes(cl); });
        if (addable.length > 0) {
            addable.forEach(function (cl) { return group.addLayer(cl); });
        }
        if (removable.length > 0) {
            removable.forEach(function (cl) { return group.removeLayer(cl); });
        }
        applyBounds();
    }, [layersState.contentLayers]);
    return (React.createElement(React.Fragment, null, children));
};
var GroupedContent$1 = forwardRef(GroupedContent);

var ZoomControl = function (_a) {
    var _b = _a.position, position = _b === void 0 ? "topleft" : _b;
    var leafletMap = useMapRefContext();
    var zoom = useState(new Control.Zoom({
        zoomInText: renderToString(React.createElement(IconPlus, { className: styles.zoomInnerLink })),
        zoomOutText: renderToString(React.createElement(IconMinus, { className: styles.zoomInnerLink })),
        position: position
    }))[0];
    useEffect(function () {
        leafletMap.addControl(zoom);
    }, [zoom]);
    return null;
};

var LayersControl = function (_a) {
    var position = _a.position;
    var isMounted = useIsMounted();
    var leafletMap = useMapRefContext();
    var layersState = useMapLayersContext().layersState;
    var _b = useState([]), layerSelections = _b[0], setLayerSelections = _b[1];
    var _c = useState(undefined), activeLayer = _c[0], setActiveLayer = _c[1];
    useEffect(function () {
        if (!activeLayer && layersState.tileLayers.length > 0)
            setActiveLayer(layersState.tileLayers[0]);
        if (layersState.tileLayers.length < 1)
            return;
        setLayerSelections(layersState.tileLayers);
    }, [layersState.tileLayers]);
    useEffect(function () {
        if (!isMounted)
            return;
        layerSelections.forEach(function (l) {
            if (leafletMap.hasLayer(l.layer))
                leafletMap.removeLayer(l.layer);
        });
        leafletMap.addLayer(activeLayer.layer);
    }, [activeLayer]);
    if (layerSelections.length <= 1)
        return null;
    var positionClasses = classNames({
        "leaflet-top": ["topleft", "topright"].includes(position),
        "leaflet-right": ["topright", "bottomright"].includes(position),
        "leaflet-bottom": ["bottomleft", "bottomright"].includes(position),
        "leaflet-left": ["topleft", "bottomleft"].includes(position)
    });
    var wrapperClasses = classNames("leaflet-control", styles.controlLayerWrapper);
    var buttonClasses = function (lRef) {
        var _a;
        return classNames((_a = {
                active: true
            },
            _a[styles.layerInnerButton] = true,
            _a[styles.layerActiveInnerButton] = lRef === activeLayer,
            _a));
    };
    return (React.createElement("div", { className: positionClasses },
        React.createElement("div", { className: wrapperClasses }, layerSelections.map(function (lRef) {
            return React.createElement("button", { key: lRef.label, type: "button", className: buttonClasses(lRef), onClick: function () { return setActiveLayer(lRef); } }, lRef.label);
        }))));
};

var DefaultTiles = function () { return (React.createElement(React.Fragment, null,
    React.createElement(TileLayer, { label: "Plan", url: DEFAULT_TILE.url, id: "mapbox/streets-v11", accessToken: DEFAULT_TILE.token, minZoom: 2, maxZoom: 18 }),
    React.createElement(TileLayer, { label: "Satellite", url: DEFAULT_TILE.url, id: "mapbox/satellite-v9", accessToken: DEFAULT_TILE.token, minZoom: 2, maxZoom: 18 }))); };
var MapContainer = function (_a, forwardedRef) {
    var id = _a.id, className = _a.className, _b = _a.height, height = _b === void 0 ? "25rem" : _b, children = _a.children, _c = _a.whenReady, whenReady = _c === void 0 ? function () { return void 0; } : _c, center = _a.center, zoom = _a.zoom, _d = _a.zoomControlPosition, zoomControlPosition = _d === void 0 ? "topleft" : _d, _e = _a.layersControlPosition, layersControlPosition = _e === void 0 ? "topright" : _e, _f = _a.fitBounds, fitBounds = _f === void 0 ? false : _f, _g = _a.clusterize, clusterize = _g === void 0 ? false : _g, _h = _a.leafletOptions, leafletOptions = _h === void 0 ? {} : _h;
    var _j = useState(undefined), mapContext = _j[0], setMapContext = _j[1];
    var wrapperProps = useState({
        id: id,
        className: classNames(styles.container, className),
        style: { height: height }
    })[0];
    useImperativeHandle(forwardedRef, function () { return mapContext !== null && mapContext !== void 0 ? mapContext : null; }, [mapContext]);
    var containerRef = createRef();
    var groupRef = useRef(null);
    var mapRef = useCallback(function (node) {
        if (node !== null && !mapContext) {
            var map = new Map(node, __assign({ layers: [], doubleClickZoom: "center", scrollWheelZoom: false, minZoom: 2, maxZoom: 18, zoomControl: false, attributionControl: false }, leafletOptions));
            map.setView(center, zoom);
            map.whenReady(whenReady);
            setMapContext(map);
        }
    }, []);
    var tiles = useMemo(function () { return React.createElement(DefaultTiles, null); }, []);
    useEffect(function () {
        mapContext === null || mapContext === void 0 ? void 0 : mapContext.invalidateSize();
        //if (!!mapContext && !!fitBounds) groupRef.current?.boundsOnResize();
    }, [containerRef]);
    useEffect(function () {
        return function () { mapContext === null || mapContext === void 0 ? void 0 : mapContext.remove(); };
    }, []);
    if (!mapContext)
        return React.createElement("div", __assign({}, wrapperProps),
            React.createElement("div", { ref: mapRef }));
    return (React.createElement("div", __assign({ ref: containerRef }, wrapperProps),
        React.createElement("div", { ref: mapRef },
            React.createElement(MapRefProvider, { value: mapContext },
                React.createElement(MapLayersProvider, null,
                    tiles,
                    React.createElement(GroupedContent$1, { ref: groupRef, fitBounds: fitBounds, clusterize: clusterize }, children),
                    React.createElement(ZoomControl, { position: zoomControlPosition }),
                    React.createElement(LayersControl, { position: layersControlPosition }))))));
};
var mapContainer = forwardRef(MapContainer);

var OverlayLayerBond = function (_a) {
    var container = _a.container, asyncAutoOpen = _a.asyncAutoOpen, className = _a.className, _b = _a.offset, offset = _b === void 0 ? [0, -8] : _b, _c = _a.closable, closable = _c === void 0 ? true : _c, children = _a.children;
    var _d = useState(false), binded = _d[0], setBinded = _d[1];
    var overlay = useState(new Popup$1({
        className: classNames(styles.popupWrapper, className),
        offset: offset,
        closeButton: closable
    }, container))[0];
    var bindPopup = function () {
        container
            .bindPopup(overlay)
            .setPopupContent(renderToString(React.createElement(React.Fragment, null, children)));
        if (asyncAutoOpen)
            container.openPopup();
        setBinded(true);
    };
    var unbindPopup = function () {
        if (container.isPopupOpen())
            container.closePopup();
        container.unbindPopup();
    };
    useEffect(function () {
        if (binded)
            unbindPopup();
        bindPopup();
    }, [container]);
    useEffect(function () {
        bindPopup();
        return function () { unbindPopup(); };
    }, []);
    return null;
};

var iconBuilder = function (content) { return new DivIcon({
    className: styles.markerWrapper,
    html: renderToString(React.createElement("div", { className: styles.markerInner }, content)),
}); };
var Marker = function (_a) {
    var position = _a.position, _b = _a.icon, htmlIcon = _b === void 0 ? (React.createElement("div", { className: styles.defaultInnerMarker })) : _b, children = _a.children, asyncPopupOnceClick = _a.asyncPopupOnceClick;
    var isMounted = useIsMounted();
    var dispatch = useMapLayersContext().dispatch;
    var marker = useState(new Marker$1(position, {
        icon: iconBuilder(htmlIcon),
        riseOnHover: true
    }))[0];
    useEffect(function () {
        dispatch({ type: "ADD_CONTENT_LAYER", layer: marker });
        !!asyncPopupOnceClick && marker.addOneTimeEventListener("click", asyncPopupOnceClick);
    }, [marker]);
    useEffect(function () {
        if (!isMounted)
            return;
        marker.setLatLng(position);
    }, [position]);
    useEffect(function () {
        if (!isMounted)
            return;
        marker.setIcon(iconBuilder(htmlIcon));
    }, [htmlIcon]);
    useEffect(function () {
        return function () { dispatch({ type: "REMOVE_CONTENT_LAYER", layer: marker }); };
    }, []);
    if (!children)
        return null;
    return (React.createElement(OverlayLayerBond, __assign({ container: marker, asyncAutoOpen: !!asyncPopupOnceClick }, children.props)));
};

var Popup = function (_) { return null; };

export { Marker, Popup, mapContainer as default };
